<script lang="ts">
import { computed, defineComponent, inject, onMounted, ref, watch } from "vue";
import Adresse from "@/components/person/Adresse.vue";
import Verwalter from "@/components/person/Verwalter.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import InfoBox from "@/components/grid/InfoBox.vue";
import Modal from "@/components/Modal.vue";
import router from "@/router";
import Logger from "js-logger";
import storeUtils from "@/businesslogic/utils/storeUtils";
import Anrede from "@/components/Anrede.vue";
import anredeUtils from "@/businesslogic/utils/anredeUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Antragsteller",
  emits: ["geburtsdatumGeaendert"],
  components: {
    InfoBox,
    Zelle,
    TitelZelle,
    Zeile,
    Ueberschrift,
    ValidierungsFehler,
    Verwalter,
    Adresse,
    Modal,
    Anrede,
  },
  props: {
    fehler: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const zeigeTarifwechselOption = ref(false);
    const versicherungsnehmerAdresseAbweichendVonRisikoadresseRef = ref<InstanceType<typeof Adresse>>();
    const versicherungsnehmerAdresseRef = ref<InstanceType<typeof Adresse>>();
    const etgWegVerwalterRef = ref<InstanceType<typeof Verwalter>>();
    const verwalterRef = ref<InstanceType<typeof Verwalter>>();
    let geburtsdatumAltNeuVergleich = "";

    const {
      istProdukt,
      gebaeudetypIstWeg,
      miteigentuemerTypIstWeg,
      istAlleineigentuemer,
      miteigentuemerTypIstEhe,
      miteigentuemerTypIstEtg,
    } = storeUtils();

    const {
      wegAnredeErlaubt,
      etgAnredeErlaubt,
      firmaAnredeErlaubt,
      anredeIstOrganisation,
      anredeIstFirma,
      anredeIstEtgOderWeg,
      anredeIstWeg,
    } = anredeUtils();

    const isFirma = computed(() => {
      return anredeIstFirma(piniaStore.state.personendaten.versicherungsnehmer.anrede);
    });

    const isOrganisation = computed(() => {
      return anredeIstOrganisation(piniaStore.state.personendaten.versicherungsnehmer.anrede);
    });

    const isEtgWeg = computed(() => {
      return anredeIstEtgOderWeg(piniaStore.state.personendaten.versicherungsnehmer.anrede);
    });

    const zeigeWegNameHinweis = computed(() => {
      return (
        istProdukt(piniaStore.state, ["bl", "bh"]) &&
        anredeIstWeg(piniaStore.state.personendaten.versicherungsnehmer.anrede)
      );
    });

    const blendeAnredeaus = computed(() => {
      return istProdukt(piniaStore.state, ["bl", "bh"]) && isEtgWeg.value;
    });

    const labelOrganisation = computed(() => {
      switch (piniaStore.state.personendaten.versicherungsnehmer.anrede) {
        case "0003":
          return "Firma";
        case "ETG":
          return "EG";
        case "WEG":
          return "WEG";
        default:
          return "";
      }
    });

    const deaktivierteFelderFuerSparte = computed(() => {
      if (istProdukt(piniaStore.state, ["phv2025", "vhv2024"])) {
        if (piniaStore.state.adresseFelderSchrittDreiDeaktivieren) {
          return ["plz", "ort", "strasse", "hausnummer"];
        }
        return ["plz"];
      }
      return [];
    });

    const verwalterFrageIstSichtbar = computed(() => {
      return istProdukt(piniaStore.state, ["vgv", "bl", "bh", "hg", "fh", "fh2024"]);
    });

    const eineTelefonnummerMussAngegebenWerden = computed(() => {
      return (
        !piniaStore.state.isMakler &&
        piniaStore.state.personendaten.versicherungsnehmer.telefon === "" &&
        piniaStore.state.personendaten.versicherungsnehmer.mobil === ""
      );
    });

    const zeigeModal = computed(() => {
      return (
        istProdukt(piniaStore.state, ["hg"]) &&
        anredeIstWeg(piniaStore.state.personendaten.versicherungsnehmer.anrede) &&
        !gebaeudetypIstWeg(piniaStore.state)
      );
    });

    const onCloseModal = () => {
      piniaStore.state.personendaten.versicherungsnehmer.anrede = "";
      zeigeTarifwechselOption.value = false;
    };

    const onChangeTarif = () => {
      piniaStore.state.personendaten.versicherungsnehmer.anrede = "";
      piniaStore.state.gebaeudedaten.alleineigentuemer = false;
      piniaStore.state.gebaeudedaten.miteigentuemerTyp = "WEG";
      router.push("/" + piniaStore.state.verkaufsprodukt.toLowerCase() + "/");
    };

    const kundeMoechteVertragsuterlagenDigital = () => {
      return piniaStore.state.personendaten.versicherungsnehmer.standardKommunikationsart === "EMAIL";
    };

    const isUndefinedOrTrue = (test: boolean | undefined) => {
      return test === undefined || test;
    };

    const pruefen = async () => {
      const versicherungsnehmerAdresseValide = await versicherungsnehmerAdresseRef.value?.pruefen(true);

      const versicherungsnehmerAdresseAbweichendVonRisikoadresseValide =
        await versicherungsnehmerAdresseAbweichendVonRisikoadresseRef.value?.pruefen(true);

      const etgWegVerwalterValide = await etgWegVerwalterRef.value?.pruefen();
      const verwalterValide = await verwalterRef.value?.pruefen();

      Logger.debug(
        "Antragssteller pruefen",
        versicherungsnehmerAdresseValide,
        versicherungsnehmerAdresseAbweichendVonRisikoadresseValide,
        etgWegVerwalterValide,
        verwalterValide,
      );

      return (
        isUndefinedOrTrue(versicherungsnehmerAdresseValide) &&
        isUndefinedOrTrue(versicherungsnehmerAdresseAbweichendVonRisikoadresseValide) &&
        isUndefinedOrTrue(etgWegVerwalterValide) &&
        isUndefinedOrTrue(verwalterValide)
      );
    };

    function onGeburtsdatumFocus() {
      geburtsdatumAltNeuVergleich = piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum;
    }

    function onGeburtsdatumBlur() {
      if (geburtsdatumAltNeuVergleich != piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum) {
        emit("geburtsdatumGeaendert");
      }
    }

    watch(
      () => piniaStore.state.personendaten.versicherungsnehmer.anrede,
      async (neueAnrede) => {
        if (anredeIstEtgOderWeg(neueAnrede)) {
          piniaStore.state.personendaten.verwalterAktiv = true;
        }
      },
    );

    watch(
      () => piniaStore.state.personendaten.versicherungsnehmer.anrede,
      () => {
        zeigeTarifwechselOption.value = zeigeModal.value;
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.miteigentuemerTyp,
      () => {
        if (miteigentuemerTypIstWeg(piniaStore.state)) {
          piniaStore.state.personendaten.versicherungsnehmer.anrede = "WEG";
        } else if (miteigentuemerTypIstEtg(piniaStore.state)) {
          piniaStore.state.personendaten.versicherungsnehmer.anrede = "ETG";
        } else if (miteigentuemerTypIstEhe(piniaStore.state)) {
          piniaStore.state.personendaten.versicherungsnehmer.anrede = "";
        }
      },
    );

    watch(
      () => [piniaStore.state.gebaeudedaten.alleineigentuemer],
      () => {
        if (istAlleineigentuemer(piniaStore.state)) {
          piniaStore.state.gebaeudedaten.miteigentuemerTyp = "";
          piniaStore.state.personendaten.versicherungsnehmer.anrede = "";
        }
      },
    );

    onMounted(() => {
      zeigeTarifwechselOption.value = zeigeModal.value;
    });

    return {
      piniaStore,
      istSichtbar,
      wechsleSichtbarkeit,
      isOrganisation,
      isFirma,
      isEtgWeg,
      labelOrganisation,
      deaktivierteFelderFuerSparte,
      verwalterFrageIstSichtbar,
      eineTelefonnummerMussAngegebenWerden,
      zeigeTarifwechselOption,
      onCloseModal,
      onChangeTarif,
      zeigeModal,
      zeigeWegNameHinweis,
      blendeAnredeaus,
      versicherungsnehmerAdresseRef,
      versicherungsnehmerAdresseAbweichendVonRisikoadresseRef,
      etgWegVerwalterRef,
      verwalterRef,
      pruefen,
      onGeburtsdatumFocus,
      onGeburtsdatumBlur,
      kundeMoechteVertragsuterlagenDigital,
      wegAnredeErlaubt,
      etgAnredeErlaubt,
      firmaAnredeErlaubt,
    };
  },
});
</script>

<template>
  <ueberschrift :titel="piniaStore.state.isMakler ? 'Antragsteller' : 'Ihre persönlichen Angaben'" />

  <zeile v-if="!blendeAnredeaus">
    <titel-zelle>
      {{
        wegAnredeErlaubt(piniaStore.state) ||
        etgAnredeErlaubt(piniaStore.state) ||
        firmaAnredeErlaubt(piniaStore.state, false)
          ? "Versicherungsnehmer"
          : "Anrede und Titel"
      }}
    </titel-zelle>
    <zelle :breite="etgAnredeErlaubt(piniaStore.state) || wegAnredeErlaubt(piniaStore.state) ? 4 : 3">
      <Anrede
        id="persSalutation"
        :fehler="fehler"
        v-model:modelValue="piniaStore.state.personendaten.versicherungsnehmer.anrede"
        :required="true"
        ariaRequired="true"
        ariaLabel="Anrede"
      />
    </zelle>
    <zelle
      :breite="etgAnredeErlaubt(piniaStore.state) || wegAnredeErlaubt(piniaStore.state) ? 2 : 3"
      v-if="!isOrganisation"
    >
      <select
        id="persTitle"
        name="persTitle"
        v-model="piniaStore.state.personendaten.versicherungsnehmer.titel"
        class="c-form-control-select"
        data-cy="versicherungsnehmerTitel"
        aria-label="Titel"
      >
        <option value="">Titel</option>
        <option value="Dipl.Ing.">Dipl. Ing.</option>
        <option value="Dr.">Dr.</option>
        <option value="Prof.Dr.">Prof. Dr.</option>
      </select>
      <validierungs-fehler :fehler-text="fehler.persTitle" :reduzierter-abstand="true" />
    </zelle>
  </zeile>

  <zeile v-if="isOrganisation">
    <titel-zelle>Name der {{ labelOrganisation }}</titel-zelle>
    <zelle :breite="6">
      <input
        type="text"
        id="organisation"
        name="organisation"
        v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.organisation"
        class="c-form-control__input"
        data-cy="versicherungsnehmerOrganisation"
        :placeholder="labelOrganisation"
        minlength="2"
        maxlength="40"
        required
        aria-required="true"
        :aria-label="labelOrganisation"
        :data-fehlermeldung="'Bitte geben Sie den Namen der ' + labelOrganisation + ' ein (2-40 Zeichen).'"
      />
      <validierungs-fehler :fehler-text="fehler.organisation" />
    </zelle>
  </zeile>

  <zeile v-if="zeigeWegNameHinweis">
    <titel-zelle />
    <zelle :breite="6">
      <div>Im Falle einer Wohnungseigentümergemeinschaft (WEG) muss diese der Versicherungsnehmer sein.</div>
    </zelle>
  </zeile>

  <verwalter v-if="isEtgWeg" :fehler="fehler" ref="etgWegVerwalterRef"></verwalter>

  <template v-if="!isEtgWeg">
    <zeile>
      <titel-zelle
        >Vorname und Name
        <template v-if="isFirma"> (optional)</template>
      </titel-zelle>
      <zelle :breite="3">
        <input
          type="text"
          id="persFirstName"
          name="persFirstName"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.vorname"
          class="c-form-control__input"
          data-cy="versicherungsnehmerVorname"
          placeholder="Vorname"
          minlength="2"
          maxlength="40"
          :required="!isFirma"
          :aria-required="!isFirma"
          aria-label="Vorname"
          data-fehlermeldung="Bitte geben Sie Ihren Vornamen ein (2-40 Zeichen)."
        />
        <validierungs-fehler :fehler-text="fehler.persFirstName" />
      </zelle>
      <zelle :breite="3">
        <input
          type="text"
          id="persLastName"
          name="persLastName"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.nachname"
          class="c-form-control__input"
          data-cy="versicherungsnehmerNachname"
          placeholder="Name"
          minlength="2"
          maxlength="40"
          :required="!isFirma"
          :aria-required="!isFirma"
          aria-label="Nachname"
          data-parsley-combined-name-length="40"
          data-fehlermeldung="Bitte geben
        Sie Ihren Nachnamen ein (2-40 Zeichen)."
        />
        <validierungs-fehler :fehler-text="fehler.persLastName" />
      </zelle>
    </zeile>
    <zeile v-if="!isFirma && !(piniaStore.state.verkaufsprodukt === 'PHV' && !piniaStore.state.isMakler)">
      <titel-zelle>Geburtsdatum</titel-zelle>
      <zelle :breite="3">
        <input
          type="date"
          id="geburtsdatum"
          name="geburtsdatum"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum"
          @focus="onGeburtsdatumFocus"
          @blur="onGeburtsdatumBlur"
          class="c-form-control__input"
          placeholder="TT.MM.JJJJ"
          title="Geburtsdatum im Format TT.MM.JJJJ"
          :required="!isFirma"
          :aria-required="!isFirma"
          aria-label="Geburtsdatum im Format TT.MM.JJJJ"
          data-checkalter18bis100="true"
          data-cy="versicherungsnehmerGeburtstag"
        />
        <validierungs-fehler :fehler-text="fehler.geburtsdatum" />
      </zelle>
    </zeile>
    <adresse
      v-if="piniaStore.state.verkaufsprodukt !== 'VGV'"
      :fehler="fehler"
      v-model:plz="piniaStore.state.personendaten.versicherungsnehmer.adresse.plz"
      v-model:ort="piniaStore.state.personendaten.versicherungsnehmer.adresse.ort"
      v-model:strasse="piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse"
      v-model:hausnummer="piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer"
      v-model:zu-pruefen="piniaStore.state.personendaten.versicherungsnehmer.adresse.zuPruefen"
      :feldNamen="{
        plz: 'versicherungsnehmerPlz',
        ort: 'versicherungsnehmerOrt',
        strasse: 'versicherungsnehmerStrasse',
        hausnummer: 'versicherungsnehmerHausnummer',
      }"
      v-bind:deaktivierte-felder="deaktivierteFelderFuerSparte"
      session-marker="versicherungsnehmer"
      ref="versicherungsnehmerAdresseRef"
    />

    <zeile>
      <titel-zelle
        >E-Mail
        {{ piniaStore.state.isMakler && !kundeMoechteVertragsuterlagenDigital() ? "(optional) " : "" }}
      </titel-zelle>
      <zelle>
        <input
          type="email"
          id="persEmail"
          name="persEmail"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.email"
          class="c-form-control__input"
          data-cy="versicherungsnehmerEmail"
          placeholder="E-Mail"
          minlength="2"
          maxlength="60"
          :required="!piniaStore.state.isMakler || kundeMoechteVertragsuterlagenDigital()"
          :aria-required="!piniaStore.state.isMakler || kundeMoechteVertragsuterlagenDigital()"
          aria-label="Email des Versicherungsnehmers"
          data-fehlermeldung="Bitte geben Sie Ihre E-Mail-Adresse ein (2-60 Zeichen)."
        />
        <validierungs-fehler :fehler-text="fehler.persEmail" />
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle :zeige-info="!piniaStore.state.isMakler" info-box-id="persTelefonInfo">
        Telefon {{ piniaStore.state.isMakler === true ? "(optional) " : "" }}
      </titel-zelle>
      <zelle>
        <input
          type="text"
          id="persPhone"
          name="persPhone"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.telefon"
          class="c-form-control__input"
          placeholder="Telefon"
          data-cy="versicherungsnehmerTelefon"
          minlength="6"
          maxlength="30"
          :required="eineTelefonnummerMussAngegebenWerden"
          :aria-required="eineTelefonnummerMussAngegebenWerden"
          aria-label="Telefonnummer des Versicherungsnehmers"
          data-checktelefonnummer="true"
        />
        <validierungs-fehler :fehler-text="fehler.persPhone" />
        <info-box id="persTelefonInfo">
          Bitte geben Sie die Telefonnummer an, unter der Sie tagsüber erreichbar sind. Ihre Daten werden nicht zu
          Werbezwecken verwendet, sondern lediglich für Rückfragen unserer Mitarbeiter/innen genutzt.
        </info-box>
      </zelle>
    </zeile>

    <zeile>
      <titel-zelle :zeige-info="!piniaStore.state.isMakler" info-box-id="persMobilInfo"
        >Mobiltelefon {{ piniaStore.state.isMakler === true ? "(optional) " : "" }}
      </titel-zelle>
      <zelle>
        <input
          type="text"
          id="persMobile"
          name="persMobile"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.mobil"
          class="c-form-control__input"
          data-cy="versicherungsnehmerMobil"
          placeholder="Mobiltelefon"
          minlength="6"
          maxlength="30"
          aria-label="Mobilrufnummer des Versicherungsnehmers"
          data-checkmobilnummer="true"
        />
        <validierungs-fehler :fehler-text="fehler.persMobile" />
        <info-box id="persMobilInfo">
          Ihre Mobilnummer wird nicht zu Werbezwecken verwendet, sondern lediglich für Rückfragen unserer
          Mitarbeiter/innen genutzt.
        </info-box>
      </zelle>
    </zeile>

    <zeile :spacing-y="false" v-if="verwalterFrageIstSichtbar">
      <titel-zelle>Abweichender Ansprechpartner/Verwalter</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.personendaten.verwalterAktiv"
          type="radio"
          id="verwalterAktivNein"
          name="verwalterAktiv"
          :value="false"
          data-cy="verwalterAktivFalse"
          required
          aria-required="true"
          aria-label="Nein, es gibt keinen abweichenden Ansprechpartner/Verwalter"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl."
        />
        <label for="verwalterAktivNein" class="radiolabel">Nein</label>
        <input
          v-model="piniaStore.state.personendaten.verwalterAktiv"
          type="radio"
          id="verwalterAktivJa"
          name="verwalterAktiv"
          :value="true"
          aria-label="Ja, es gibt einen abweichenden Ansprechpartner/Verwalter"
          data-cy="verwalterAktivTrue"
        />
        <label for="verwalterAktivJa" class="radiolabel"> Ja</label>
        <validierungs-fehler :fehler-text="fehler.verwalterAktiv" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <verwalter v-if="piniaStore.state.personendaten.verwalterAktiv" :fehler="fehler" ref="verwalterRef" />

    <template v-if="piniaStore.state.verkaufsprodukt === 'VGV' && !piniaStore.state.personendaten.verwalterAktiv">
      <zeile :spacing-y="false">
        <titel-zelle>Abweichende Postanschrift</titel-zelle>
        <zelle class="mkRadio">
          <input
            v-model="piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse"
            type="radio"
            id="adresseAbweichendVonRisikoadresseNein"
            name="adresseAbweichendVonRisikoadresse"
            :value="false"
            data-cy="adresseAbweichendVonRisikoadresseFalse"
            required
            data-fehlermeldung="Bitte wählen Sie eine Zahlart aus."
          />
          <label for="adresseAbweichendVonRisikoadresseNein" class="radiolabel">Nein</label>
          <input
            v-model="piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse"
            type="radio"
            id="adresseAbweichendVonRisikoadresseJa"
            name="adresseAbweichendVonRisikoadresse"
            :value="true"
            data-cy="adresseAbweichendVonRisikoadresseTrue"
          />
          <label for="adresseAbweichendVonRisikoadresseJa" class="radiolabel">Ja</label>
          <validierungs-fehler :fehler-text="fehler.adresseAbweichendVonRisikoadresse" :reduzierter-abstand="true" />
        </zelle>
      </zeile>
      <adresse
        v-if="piniaStore.state.personendaten.versicherungsnehmer.adresseAbweichendVonRisikoadresse"
        :fehler="fehler"
        v-model:plz="piniaStore.state.personendaten.versicherungsnehmer.adresse.plz"
        v-model:ort="piniaStore.state.personendaten.versicherungsnehmer.adresse.ort"
        v-model:strasse="piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse"
        v-model:hausnummer="piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer"
        v-model:zu-pruefen="piniaStore.state.personendaten.versicherungsnehmer.adresse.zuPruefen"
        :feldNamen="{
          plz: 'abweichendePlz',
          ort: 'abweichenderOrt',
          strasse: 'abweichendeStrasse',
          hausnummer: 'abweichendeHausnummer',
        }"
        session-marker="versicherungsnehmer"
        ref="versicherungsnehmerAdresseAbweichendVonRisikoadresseRef"
      />
    </template>
  </template>
  <modal
    v-if="zeigeTarifwechselOption"
    @closeModal="onCloseModal"
    titel="Wohnungseigentümergemeinschaft (WEG)"
    text="Für Wohnungseigentümergemeinschaften haben wir spezielle Tarife. Die entsprechende Auswahl können Sie auf Schritt 1 treffen."
  >
    <template v-slot:inhalt>
      <div class="o-grid__cell o-grid__cell--12 u-text-align-right">
        <div class="o-grid">
          <div class="o-grid__cell o-grid__cell--6 form-actions u-mar-b-0 u-text-align-left">
            <button class="c-button c-button--inverted" type="button" @click="onCloseModal">Abbrechen</button>
          </div>
          <div class="o-grid__cell o-grid__cell--6 form-actions u-mar-b-0 u-text-align-right">
            <button class="c-button right" type="button" @click="onChangeTarif">WEG-Tarif auf Schritt 1 wählen</button>
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>
